/**
 * Valores de `events_guests.confirmation_status` y los dos conjuntos con los que hay que leerlos.
 *
 * EL PROBLEMA QUE RESUELVE: la misma respuesta del invitado se guarda con palabras distintas según
 * la pantalla por la que entre. «He dicho que sí» es `accepted` si respondió desde el enlace del
 * correo (SPA pública) y `confirmed` si lo hizo desde su zona de usuario, desde el área MVC antigua
 * o desde este backoffice. «No puedo ir» es `rejected` o `excused` por el mismo motivo.
 *
 * Comparar contra un literal suelto no falla nunca: cuenta de menos, en silencio. En el backoffice
 * eso se veía como contadores que no sumaban el total, un invitado que fichaba apareciendo como
 * «no esperado», y una etiqueta de estado sin color (ITSM #1839288).
 *
 * Es el espejo de `Stela.Domain/Constants/GuestConfirmationStatuses.cs`. Si allí cambia el
 * repertorio, aquí también. No es la centralización completa (ITSM #1839290), sólo su semilla.
 *
 * OJO: esto es para LEER. Al escribir se sigue mandando un literal concreto, y cuál sea el canónico
 * lo decide ITSM #1839290.
 */
export const GUEST_STATUS = {
    /** Aún no ha respondido. */
    PENDING: 'pending',
    /** Ha dicho que sí. Zona de usuario, área MVC y backoffice. */
    CONFIRMED: 'confirmed',
    /** Ha dicho que sí. SÓLO la SPA pública. */
    ACCEPTED: 'accepted',
    /** No puede ir. SPA pública, área MVC y backoffice. */
    REJECTED: 'rejected',
    /** No puede ir. SÓLO la zona de usuario. */
    EXCUSED: 'excused',
    /** Ha delegado su invitación: estado del titular. */
    DELEGATED: 'delegated',
    /** Estado de la persona en quien se delegó. */
    MANAGE_DELEGATE: 'manage_delegate',
    /** Acompañante de la pública, a la espera de aprobación. */
    PENDING_APPROVAL: 'pending_approval',
    /** Se ha dado de baja de los envíos. */
    UNSUBSCRIBED: 'unsubscribed'
} as const;

/** Los dos valores que significan que el invitado ASISTE. */
const MEANS_YES: readonly string[] = [GUEST_STATUS.CONFIRMED, GUEST_STATUS.ACCEPTED];

/**
 * Los dos que significan que NO asiste.
 *
 * `unsubscribed` no entra: darse de baja de los envíos no es responder que no, y meterlo en el
 * mismo cubo cambiaría el significado de una cifra. Eso es decisión de producto.
 */
const MEANS_NO: readonly string[] = [GUEST_STATUS.REJECTED, GUEST_STATUS.EXCUSED];

/** ¿Este invitado ha dicho que sí, lo escribiera quien lo escribiera? */
export function guestSaidYes(status: string | null | undefined): boolean {
    return !!status && MEANS_YES.includes(status);
}

/** ¿Este invitado ha dicho que no? */
export function guestSaidNo(status: string | null | undefined): boolean {
    return !!status && MEANS_NO.includes(status);
}

/** Todos los literales que significan «sí», para filtros que reciben una lista de estados. */
export const GUEST_STATUSES_MEANING_YES: readonly string[] = MEANS_YES;
